import React, { useCallback, useMemo } from "react";
import { Redirect, Route, Switch, useHistory, useLocation, useParams } from "react-router-dom";
import config from "config";
import useAuthUser from "hooks/useAuthUser";
import useCustomDomain from "hooks/useCustomDomain";
import ClientLogin from "modals/ClientLogin";
import ClientSignUp from "modals/ClientSignUp";
import ConfirmEmail from "modals/ConfirmEmail";
import EmailVerificationByCode from "modals/EmailVerificationByCode";
import ForgotPassword from "modals/ForgotPassword";
import GoAppointmentChatModal from "modals/GoAppointmentChatModal";
import GoConferenceModal from "modals/GoConferenceModal";
import { LocationState } from "types";
import ModalWindow from "components/ModalWindow";
import ScrollToTopOnMount from "components/ScrollToTopOnMount";
import PageAppointmentChat from "pages/PageAppointmentChat";
import PageAuth from "pages/PageAuth";
import PageConferenceChat from "pages/PageConferenceChat";
import PageDirectCall from "pages/PageDirectCall";
import PageJoinGroup from "pages/PageJoinGroup";
import PageFeatures from "pages/PageFeatures";
import PageLanding from "pages/PageLanding";
import PageTerms from "pages/PageTerms";
import PagePrivacy from "pages/PagePrivacy";
import PageLiveChat from "pages/PageLiveChat";
import PageLoginToken from "pages/PageLoginToken";
import PageLoginTwitter from "pages/PageLoginTwitter";
import PageLogout from "pages/PageLogout";
import PageMessages from "pages/PageMessages";
import PagePerformerProfile from "pages/PagePerformerProfile";
import PagePerformers from "pages/PagePerformers";
import PageSettings from "pages/PageUserArea";
import { Routes } from "../config";
import LinkedModalPageView from "../LinkedModalPageView";

interface LinkedModalDescription {
  path: string;
  Component: React.FC;
  meta?: {
    authOnly?: boolean;
    modal?: boolean;
  };
  comparator?: (currentPath: string) => boolean;
}

interface RouterDescription {
  path: string;
  exact?: boolean;
  Component: React.FC;
}

// Stable component identity — must live at module scope. Defining this
// inside an IIFE inside MainRouterView's render tree (as we did until
// 2026-06-17) created a fresh function reference on EVERY MainRouterView
// render. React treats a new function reference as a new child-element
// type and tears down + remounts the entire subtree underneath — in our
// case the live PageDirectCall, killing the WebRTC transports and forcing
// a reconnect blip every time a parent setState/context update fired.
// Lifecycle dump from a 1-on-1 voice-only call (1261) showed instance#1
// PageDirectCall MOUNT, then 2:47 later instance#2 MOUNT immediately
// followed by instance#1 UNMOUNT — same locKey, same pathname, same
// userId, no location.key change. Classic IIFE remount fingerprint.
function CallRoute() {
  const { callId } = useParams<{ callId: string }>();
  // key={callId}: PageDirectCall / useCallController keep all call state
  // (connectionRef, acceptedRef, callPhase, streamsStartedRef…) per-mount and
  // cannot re-initialise on a callId prop change. Accepting a second call
  // while on /call/<first> (history.push to /call/<second>) hits the same
  // Route/component and would reuse the first call's stale state. Keying on
  // callId forces a clean remount per call — while STILL preserving the live
  // call across unrelated parent re-renders (the concern the CallRoute
  // extraction above solved), since the key is stable for a given call.
  return <PageDirectCall key={callId} callId={callId} />;
}

export default function MainRouterView() {
  const location = useLocation<LocationState>();
  const history = useHistory<LocationState>();
  const backgroundLocation = location.state?.location;
  const onModalClose = useCallback(() => history.replace(backgroundLocation || { pathname: Routes.root }), [
    history,
    backgroundLocation,
  ]);

  const modals: LinkedModalDescription[] = useMemo(
    () => [
      {
        path: Routes.login,
        Component: () => <ClientLogin onClose={onModalClose} />,
      },
      {
        path: Routes.loginForgot,
        Component: () => <ForgotPassword onCancel={onModalClose} />,
      },
      {
        path: Routes.loginForgotByEmail,
        Component: () => {
          const { email } = useParams<{ email: string }>();
          return <ForgotPassword email={email} onCancel={onModalClose} />;
        },
      },
      {
        path: Routes.loginForgotByEmailByToken,
        Component: () => {
          const { token } = useParams<{ token: string }>();
          return <ForgotPassword token={token} onCancel={onModalClose} />;
        },
      },
      {
        path: Routes.signup,
        Component: () => <ClientSignUp onClose={onModalClose} />,
      },
      {
        path: Routes.signupEmail,
        Component: () => <ConfirmEmail onCancel={onModalClose} />,
        meta: {
          modal: location.pathname === Routes.signupEmail,
        },
      },
      {
        path: Routes.signupConfirm,
        Component: () => {
          const search = new URLSearchParams(location.search);
          const code = search.get("code");
          return <EmailVerificationByCode onClose={onModalClose} code={code} />;
        },
        meta: {
          modal: location.pathname === Routes.signupConfirm,
        },
      },
      {
        path: Routes.appointmentJoinByAppointmentId,
        Component: () => {
          const { appointmentId } = useParams<{ appointmentId: string }>();
          return <GoAppointmentChatModal onClose={onModalClose} appointmentId={appointmentId} />;
        },
        meta: { modal: true },
        comparator: (currentPath) => /\/appointment\/join\/\d+/.test(currentPath),
      },
      {
        path: Routes.conferenceJoinByConferenceUuid,
        Component: () => {
          const { conferenceUuid } = useParams<{ conferenceUuid: string }>();
          return <GoConferenceModal onClose={onModalClose} conferenceUuid={conferenceUuid} />;
        },
        meta: { modal: true },
        comparator: (currentPath) => /\/conference\/join\/\d+/.test(currentPath),
      },
    ],
    [location, onModalClose]
  );

  const { user, isCustomDomain } = useCustomDomain();

  const routes = useMemo(() => {
    const routes: RouterDescription[] = [];

    if (isCustomDomain) {
      routes.push({
        path: Routes.live,
        Component: () => <PageLiveChat username={user ? user.username : ""} />,
      });
    } else {
      routes.push(
        {
          path: Routes.home,
          exact: true,
          Component: () => <PagePerformers key="performers" />,
        },
        {
          path: Routes.favorites,
          Component: () => <PagePerformers onlyFollowings key="favorites" />,
        }
      );
    }

    if (config.featureLoginAsUser) {
      routes.push({
        path: Routes.loginTokenByToken,
        Component: () => {
          const { token } = useParams<{ token: string }>();
          return <PageLoginToken token={token} />;
        },
      });
    }

    routes.push(
      {
        path: Routes.conferenceByConferenceUuid,
        Component: () => {
          const { conferenceUuid } = useParams<{ conferenceUuid: string }>();
          return <PageConferenceChat conferenceUuid={conferenceUuid} />;
        },
      },
      {
        path: Routes.liveByUsername,
        Component: () => {
          const { username } = useParams<{ username: string }>();
          return <PageLiveChat username={username} />;
        },
      },
      {
        path: Routes.rootByUsername,
        Component: () => {
          const { username } = useParams<{ username: string }>();
          return <PagePerformerProfile username={username} />;
        },
      },
      {
        path: Routes.root,
        Component: () => {
          const { user: authUser } = useAuthUser();
          if (isCustomDomain) {
            return <PagePerformerProfile username={user ? user.username : ""} />;
          }
          if (authUser && authUser.emailVerified) {
            return <Redirect to={Routes.messages} />;
          }
          return <Redirect to={Routes.landing} />;
        },
      }
    );

    return routes;
  }, [isCustomDomain, user]);

  const currentModal = modals.find((m) =>
    m.comparator ? m.comparator(location.pathname) : m.path === location.pathname
  );

  return (
    <>
      <Switch location={backgroundLocation || location}>
        {!backgroundLocation &&
          modals.map((data) => (
            <Route path={data.path} exact key={data.path}>
              <LinkedModalPageView path={location.pathname} />
            </Route>
          ))}
        <Route path={Routes.landing} exact>
          <ScrollToTopOnMount />
          <PageLanding />
        </Route>
        <Route path={Routes.features} exact>
          <ScrollToTopOnMount />
          <PageFeatures />
        </Route>
        <Route path={Routes.terms} exact>
          <ScrollToTopOnMount />
          <PageTerms />
        </Route>
        <Route path={Routes.privacy} exact>
          <ScrollToTopOnMount />
          <PagePrivacy />
        </Route>
        <Route path={Routes.user}>
          <ScrollToTopOnMount />
          <PageSettings />
        </Route>
        <Route path={Routes.logout}>
          <PageLogout />
        </Route>
        <Route path={Routes.loginTwitter}>
          <PageLoginTwitter />
        </Route>
        <Route path={Routes.login}>
          <PageAuth />
        </Route>
        <Route path={Routes.signup}>
          <PageAuth />
        </Route>
        <Route path={Routes.groupJoinById}>
          <PageJoinGroup />
        </Route>
        <Route path={Routes.messages}>
          <PageMessages />
        </Route>
        <Route path={Routes.callByCallId}>
          <CallRoute />
        </Route>
        <Route path={Routes.appointmentByAppointmentId}>
          <PageAppointmentChat />
        </Route>
        {routes.map(({ path, exact, Component }) => (
          <Route path={path} exact={exact} key={path}>
            <ScrollToTopOnMount />
            <Component />
          </Route>
        ))}
      </Switch>
      {backgroundLocation && (
        <ModalWindow show modal={currentModal?.meta?.modal} onClose={onModalClose}>
          {modals.map(({ path, Component }) => (
            <Route path={path} exact key={path}>
              <Component />
            </Route>
          ))}
        </ModalWindow>
      )}
    </>
  );
}
